import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Calendar, Filter, Menu, X, Moon, Sun, Bell, BellOff } from 'lucide-react';
import AuthScreen from './shared/AuthScreen';
import KPISection from './schoorsteenveger/KPISection';
import RevenueChart from './schoorsteenveger/RevenueChart';
import SvFunnelSection from './schoorsteenveger/FunnelSection';
import RoutesSection from './schoorsteenveger/RoutesSection';
import RealisatieSection from './schoorsteenveger/RealisatieSection';
import PipelineAnalytics from './schoorsteenveger/PipelineAnalytics';
import TeamLeaderboard from './schoorsteenveger/TeamLeaderboard';
import ServicesBreakdown from './schoorsteenveger/ServicesBreakdown';
import OutstandingInvoices from './schoorsteenveger/OutstandingInvoices';
import { getAllDashboardData, getFunnelData, getRoutesData } from '../utils/schoorsteenvegerQueries';
import { apiPost, setToken, hasValidToken } from '../utils/apiClient';
import { dashboardCache } from '../utils/performanceCache';
import { useAudio } from '../hooks/useAudio';
import { useDarkMode } from '../hooks/useDarkMode';

// Menu-paneel (alle schermformaten): periode-keuze + auto-refresh. De vaste
// periodebalk is vervangen door hamburger + periode-chip in de header — het
// dashboard blijft clean wanneer het open staat op een scherm.
const PeriodSelector = ({ selectedPeriod, onPeriodChange, onCustomDateChange, customStartDate, customEndDate, isMobileMenuOpen, setIsMobileMenuOpen, autoRefresh, setAutoRefresh, muted, setMuted }) => {

  const periods = [
    { value: 'today', label: 'Vandaag' },
    { value: 'yesterday', label: 'Gisteren' },
    { value: 'thisWeek', label: 'Deze week' },
    { value: 'lastWeek', label: 'Vorige week' },
    { value: 'thisMonth', label: 'Deze maand' },
    { value: 'lastMonth', label: 'Vorige maand' },
    { value: 'thisYear', label: 'Dit jaar' }
  ];

  return (
    <>
      {isMobileMenuOpen && (
        <div className="mobile-menu-blue">
          <div className="menu-section">
            <div className="menu-label">
              <Filter className="w-4 h-4" />
              <span>Periode selecteren:</span>
            </div>
            <div className="period-grid">
              {periods.map((period) => (
                <button
                  key={period.value}
                  onClick={() => {
                    onPeriodChange(period.value);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`period-btn ${selectedPeriod === period.value ? 'active' : ''}`}
                  style={{
                    padding: 'var(--space-2) var(--space-3)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    cursor: 'pointer',
                    background: selectedPeriod === period.value ? 'var(--color-blue-500)' : 'var(--color-neutral-100)',
                    color: selectedPeriod === period.value ? 'white' : 'var(--color-neutral-700)'
                  }}
                >
                  {period.label}
                </button>
              ))}
            </div>
          </div>
          <div className="menu-section" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Calendar className="w-4 h-4" style={{ color: 'var(--color-neutral-500)' }} />
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => onCustomDateChange('start', e.target.value)}
              className="date-input"
              style={{
                padding: 'var(--space-2)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--color-neutral-300)',
                borderRadius: 'var(--radius-md)',
                flex: 1
              }}
              placeholder="Van"
            />
            <span style={{ color: 'var(--color-neutral-500)', fontSize: 'var(--text-sm)' }}>tot</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => onCustomDateChange('end', e.target.value)}
              className="date-input"
              style={{
                padding: 'var(--space-2)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--color-neutral-300)',
                borderRadius: 'var(--radius-md)',
                flex: 1
              }}
              placeholder="Tot"
            />
          </div>
          <div className="menu-section" style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              type="button"
              onClick={() => setAutoRefresh((p) => !p)}
              title={autoRefresh ? 'Automatisch verversen staat aan (elke 5 min)' : 'Automatisch verversen (elke 5 min)'}
              aria-pressed={autoRefresh}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                padding: 'var(--space-2)', width: '38px', height: '34px',
                borderRadius: 'var(--radius-md)', border: 'none', cursor: 'pointer',
                background: autoRefresh ? 'var(--color-blue-500)' : 'var(--color-neutral-100)',
                color: autoRefresh ? 'white' : 'var(--color-neutral-600)',
              }}
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setMuted((p) => !p)}
              title={muted ? 'Geluid staat uit' : 'Geluid dempen'}
              aria-pressed={muted}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                padding: 'var(--space-2)', width: '38px', height: '34px',
                borderRadius: 'var(--radius-md)', border: 'none', cursor: 'pointer',
                background: muted ? 'var(--color-blue-500)' : 'var(--color-neutral-100)',
                color: muted ? 'white' : 'var(--color-neutral-600)',
              }}
            >
              {muted ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const SchoorsteenvegerDashboard = () => {
  // Audio hook
  const { playAudio, AudioElement } = useAudio('/betalen.mp3');

  // Authentication state
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');

  // Data state
  const [dashboardData, setDashboardData] = useState({
    currentMonthRevenue: 0,
    currentMonthInvoiceCount: 0,
    totalOutstanding: 0,
    averageInvoiceValue: 0,
    employeeData: [],
    serviceData: [],
    outstandingInvoices: [],
    monthlyTrend: [],
    funnel: null
  });
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  // RouteLogic-routes: periode-onafhankelijk, eenmalig laden (server cachet)
  const [routesData, setRoutesData] = useState(null);
  const [routesLoading, setRoutesLoading] = useState(false);

  // Dark mode: systeeminstelling, tenzij expliciet getoggeld (dan persistent)
  const [isDark, setIsDark] = useDarkMode();

  // Auto-refresh (elke 5 min — de bron-syncs draaien toch niet vaker), persistent
  const [autoRefresh, setAutoRefreshState] = useState(() => {
    try { return localStorage.getItem('sv_auto_refresh') === 'true'; } catch { return false; }
  });
  const setAutoRefresh = useCallback((v) => {
    setAutoRefreshState((prev) => {
      const next = typeof v === 'function' ? v(prev) : v;
      try { localStorage.setItem('sv_auto_refresh', String(next)); } catch { /* private mode */ }
      return next;
    });
  }, []);

  // Mute (persistent): dempt het betalen-geluid bij handmatige acties.
  // Auto-refresh speelt sowieso nooit geluid af.
  const [muted, setMutedState] = useState(() => {
    try { return localStorage.getItem('sv_muted') === 'true'; } catch { return false; }
  });
  const mutedRef = useRef(muted);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  const setMuted = useCallback((v) => {
    setMutedState((prev) => {
      const next = typeof v === 'function' ? v(prev) : v;
      try { localStorage.setItem('sv_muted', String(next)); } catch { /* private mode */ }
      return next;
    });
  }, []);

  // Filter state
  const [filters, setFilters] = useState({
    period: 'today',
    startDate: null,
    endDate: null,
    customPeriod: false
  });

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Authentication handlers — login via backend levert een JWT op
  const handlePasswordSubmit = useCallback(async () => {
    try {
      const res = await apiPost('/login', { type: 'schoorsteenveger', password });
      setToken(res.token);
      setIsAuthenticated(true);
      setAuthError('');
    } catch (error) {
      setAuthError('Onjuist wachtwoord');
    }
  }, [password]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handlePasswordSubmit();
    }
  }, [handlePasswordSubmit]);

  // Check authentication on mount — geldige (niet-verlopen) JWT?
  useEffect(() => {
    setIsAuthenticated(hasValidToken());
  }, []);

  // Geluid alleen bij handmatige acties én niet gedempt (auto-refresh is stil)
  const playSound = useCallback(() => {
    if (!mutedRef.current) playAudio();
  }, [playAudio]);

  // Play audio when authentication is successful
  useEffect(() => {
    if (isAuthenticated) {
      setTimeout(() => {
        playSound();
      }, 100);
    }
  }, [isAuthenticated, playSound]);

  // Period change handler
  const handlePeriodChange = useCallback((period) => {
    setFilters(prev => ({
      ...prev,
      period,
      customPeriod: false,
      startDate: null,
      endDate: null
    }));
  }, []);

  // Custom date change handler
  const handleCustomDateChange = useCallback((type, value) => {
    setFilters(prev => ({
      ...prev,
      [type === 'start' ? 'startDate' : 'endDate']: value,
      customPeriod: true,
      period: 'custom'
    }));
  }, []);

  // Get period label for KPI subtitles
  const getPeriodLabel = useCallback((period) => {
    const labels = {
      'today': 'Vandaag',
      'yesterday': 'Gisteren',
      'thisWeek': 'Deze week',
      'lastWeek': 'Vorige week',
      'thisMonth': 'Deze maand',
      'lastMonth': 'Vorige maand',
      'thisYear': 'Dit jaar',
      'custom': 'Aangepaste periode'
    };
    return labels[period] || 'Deze maand';
  }, []);

  // Data loading function; silent = geen geluid (gebruikt door auto-refresh)
  const loadData = useCallback(async (silent = false) => {
    if (!filters.customPeriod) {
      const cachedData = dashboardCache.get({ period: filters.period, startDate: filters.startDate, endDate: filters.endDate });
      if (cachedData) {
        setDashboardData(cachedData);
        setLastRefresh(new Date());
        if (!silent) playSound();
        return;
      }
    }

    setLoading(true);

    try {
      // Funnel parallel aan de bestaande bundel; getFunnelData geeft null bij
      // een fout zodat de rest van het dashboard gewoon laadt.
      const [allData, funnel] = await Promise.all([
        getAllDashboardData(filters),
        getFunnelData(filters),
      ]);
      const data = { ...allData, funnel };

      setDashboardData(data);
      setLastRefresh(new Date());
      if (!silent) playSound();

      if (!filters.customPeriod) {
        dashboardCache.set({ period: filters.period, startDate: filters.startDate, endDate: filters.endDate }, data);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, playSound]);

  // Load data when filters change
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, filters, loadData]);

  // Routes eenmalig laden na login (los van het periode-filter)
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setRoutesLoading(true);
    getRoutesData().then((r) => {
      if (!cancelled) { setRoutesData(r); setRoutesLoading(false); }
    });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Manual refresh handler
  const handleRefresh = useCallback(() => {
    dashboardCache.clear();
    loadData();
    getRoutesData().then((r) => { if (r) setRoutesData(r); });
  }, [loadData]);

  // Auto-refresh: elke 5 minuten alles stil verversen (KPI's + funnel + routes)
  useEffect(() => {
    if (!isAuthenticated || !autoRefresh) return undefined;
    const interval = setInterval(() => {
      dashboardCache.clear();
      loadData(true); // silent: geen betalen-geluid bij automatische refresh
      getRoutesData().then((r) => { if (r) setRoutesData(r); });
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, autoRefresh, loadData]);

  if (!isAuthenticated) {
    return (
      <AuthScreen
        auth={{
          password,
          setPassword,
          authError,
          handlePasswordSubmit,
          handleKeyPress,
          authType: 'Schoorsteenveger Dashboard',
          loading: false
        }}
        theme="blue"
      />
    );
  }

  return (
    <div className="theme-blue-container" style={{ minHeight: '100vh', background: 'var(--color-neutral-50)' }}>
      {/* Sticky Header */}
      <header className="dashboard-header-blue">
        <div className="header-content">
          <div className="header-brand">
            <div className="header-logo">
              <img
                src="https://mrschoorsteenveger.nl/wp-content/uploads/Mr.-Schoorsteenveger.png"
                alt="Mr. Schoorsteenveger Logo"
              />
            </div>
            <div>
              <h1 className="header-title">Mr. Schoorsteenveger</h1>
              {lastRefresh && (
                <span className="header-subtitle">
                  {lastRefresh.toLocaleDateString('nl-NL')} {lastRefresh.toLocaleTimeString('nl-NL')}
                </span>
              )}
            </div>
          </div>
          <div className="header-actions">
            {/* Actieve periode als chip (desktop; mobiel staat de periode al in
                de KPI-subtitels en is de header te krap); klik opent het menu */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="btn-header sv-period-chip"
              title="Periode wijzigen"
              style={{ alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', fontWeight: 500, padding: 'var(--space-2) var(--space-3)', whiteSpace: 'nowrap' }}
            >
              <Filter className="w-4 h-4" />
              {getPeriodLabel(filters.period)}
            </button>
            {/* Menu-toggle: periode + auto-refresh (alle schermformaten) */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="btn-header"
              title="Menu"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <button
              onClick={() => setIsDark((prev) => !prev)}
              className="btn-header"
              title={isDark ? 'Lichte modus' : 'Donkere modus'}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="btn-header"
              title="Vernieuwen"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content — volle breedte op desktop (zoals call-tracking) */}
      <main style={{ margin: '0 auto', padding: 'var(--space-6) var(--space-4)' }}>
        {/* Period Selector */}
        <PeriodSelector
          selectedPeriod={filters.period}
          onPeriodChange={handlePeriodChange}
          onCustomDateChange={handleCustomDateChange}
          customStartDate={filters.startDate || ''}
          customEndDate={filters.endDate || ''}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          autoRefresh={autoRefresh}
          setAutoRefresh={setAutoRefresh}
          muted={muted}
          setMuted={setMuted}
        />

        {/* KPI Section */}
        <KPISection
          currentMonthRevenue={dashboardData.currentMonthRevenue}
          currentMonthInvoiceCount={dashboardData.currentMonthInvoiceCount}
          totalOutstanding={dashboardData.totalOutstanding}
          averageInvoiceValue={dashboardData.averageInvoiceValue}
          loading={loading}
          periodLabel={getPeriodLabel(filters.period)}
        />

        {/* Funnel: sessies -> aanvragen -> ingepland -> uitgevoerd -> omzet */}
        <SvFunnelSection data={dashboardData.funnel} loading={loading} />

        {/* RouteLogic: routekaart + omzetprognose planning (vast, periode-onafhankelijk) */}
        <RoutesSection data={routesData} loading={routesLoading} isDark={isDark} />

        {/* Charts Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(1, 1fr)',
          gap: 'var(--space-6)',
          marginBottom: 'var(--space-6)'
        }} className="lg:grid-cols-2">
          <TeamLeaderboard data={dashboardData.employeeData} loading={loading} period={filters.period} />
          <ServicesBreakdown data={dashboardData.serviceData} loading={loading} />
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(1, 1fr)',
          gap: 'var(--space-6)'
        }} className="lg:grid-cols-2">
          <RevenueChart data={dashboardData.monthlyTrend} loading={loading} period={filters.period} />
          <OutstandingInvoices data={dashboardData.outstandingInvoices} loading={loading} />
        </div>

        {/* TrackJack realisatie: dag-tijdlijn + gepland vs gereden (verbergt
            zichzelf zolang de TrackJack-koppeling er niet is) */}
        <RealisatieSection isDark={isDark} />

        {/* Pipeline-analyse: hele historie, bewust helemaal onderaan en
            dichtgeklapt (analyse, geen dagelijkse operatie); bevat ook de
            provincie-kaart */}
        <PipelineAnalytics filters={filters} isDark={isDark} />
      </main>

      {/* Audio element */}
      <AudioElement />

      {/* Responsive grid styles */}
      <style>{`
        @media (min-width: 1024px) {
          .lg\\:grid-cols-2 {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .lg\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SchoorsteenvegerDashboard;
